import { type FC } from 'react'

import { F0Box, F0Button, F0Dialog, F0Heading, F0Text } from '@factorialco/f0-react'
import { Basket } from '@factorialco/f0-react/icons/app'

import formatCurrency from 'modules/core/lib/formatCurrency'
import { toCents } from 'modules/core/lib/formatCurrencyUtils'
import i18n from 'modules/core/lib/i18n'
import type { CartItem } from 'modules/it_management/hooks/device_catalog/types'

import { CartItemRow } from './CartItemRow'

export type MarketplaceCartSidepanelProps = {
  isOpen: boolean
  items: CartItem[]
  subtotal: number
  total: number
  itemCount: number
  onClose: () => void
  onUpdateQuantity: (variantId: string, offerId: string, quantity: number) => void
  onRemoveItem: (variantId: string, offerId: string) => void
  onCheckout: () => void
  isCheckingOut: boolean
}

function formatPrice(amount: number): string {
  return formatCurrency(toCents(amount))
}

export const MarketplaceCartSidepanel: FC<MarketplaceCartSidepanelProps> = ({
  isOpen,
  items,
  subtotal,
  total,
  itemCount,
  onClose,
  onUpdateQuantity,
  onRemoveItem,
  onCheckout,
  isCheckingOut,
}) => {
  if (!isOpen) return null

  const isEmpty = items.length === 0

  const subtotalLabel =
    itemCount === 1
      ? i18n.t('it_management.device_catalog.cart.subtotal', { count: itemCount })
      : i18n.t('it_management.device_catalog.cart.subtotal_other', { count: itemCount })

  return (
    <F0Dialog
      isOpen
      onClose={onClose}
      title={i18n.t('it_management.device_catalog.cart.title')}
      position="right"
      disableContentPadding
    >
      <div className="flex min-h-full flex-col">
        {/* Cart items area — dialog viewport scrolls, footer remains sticky */}
        <div
          data-testid="cart-items-scroll-area"
          className="flex min-h-0 flex-1 flex-col px-2 pt-4 pb-4"
        >
          {isEmpty ? (
            <div className="flex flex-col items-center justify-center px-2 py-12">
              <F0Heading
                variant="heading"
                content={i18n.t('it_management.device_catalog.cart.empty_title')}
              />
              <F0Text
                variant="description"
                content={i18n.t('it_management.device_catalog.cart.empty_description')}
              />
            </div>
          ) : (
            items.map((item) => (
              <div key={`${item.variantId}-${item.offerId}`} className="shrink-0 px-2">
                <CartItemRow
                  item={item}
                  onUpdateQuantity={onUpdateQuantity}
                  onRemove={onRemoveItem}
                />
              </div>
            ))
          )}
        </div>

        {!isEmpty && (
          <div className="sticky bottom-0 z-20 flex shrink-0 flex-col bg-f1-background pb-2 shadow-[0_-1px_0_0_var(--color-f1-border-secondary)]">
            <F0Box display="flex" flexDirection="column" gap="md">
              <hr className="m-0 border-t border-f1-border-secondary" />

              {/* Content with padding */}
              <div className="px-4">
                <F0Box
                  display="flex"
                  flexDirection="row"
                  justifyContent="between"
                  alignItems="center"
                >
                  <F0Text variant="description" content={subtotalLabel} />
                  <F0Text variant="label" content={formatPrice(subtotal)} />
                </F0Box>

                <F0Box
                  display="flex"
                  flexDirection="row"
                  justifyContent="between"
                  alignItems="center"
                  paddingTop="md"
                >
                  <F0Text
                    variant="description"
                    content={i18n.t('it_management.device_catalog.cart.estimated_vat')}
                  />
                  <F0Text
                    variant="description"
                    content={i18n.t('it_management.device_catalog.cart.vat_calculated')}
                  />
                </F0Box>
              </div>

              <hr className="m-0 border-t border-f1-border-secondary" />

              {/* Content with padding */}
              <div className="px-4">
                <F0Box
                  display="flex"
                  flexDirection="row"
                  justifyContent="between"
                  alignItems="center"
                >
                  <F0Text
                    variant="label"
                    content={i18n.t('it_management.device_catalog.cart.total')}
                  />
                  <F0Heading variant="heading" content={formatPrice(total)} />
                </F0Box>

                <F0Box
                  display="flex"
                  flexDirection="row"
                  gap="md"
                  justifyContent="end"
                  paddingTop="lg"
                >
                  <F0Button
                    variant="outline"
                    label={i18n.t('it_management.device_catalog.cart.cancel')}
                    onClick={onClose}
                  />
                  <F0Button
                    label={i18n.t('it_management.device_catalog.cart.proceed_to_checkout')}
                    icon={Basket}
                    onClick={onCheckout}
                    disabled={isCheckingOut}
                  />
                </F0Box>
              </div>
            </F0Box>
          </div>
        )}
      </div>
    </F0Dialog>
  )
}
